import React, { useState } from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton
} from '@material-ui/core'
import { Clear } from '@material-ui/icons'
import { createDateString } from 'helpers'

const extractCancellationTimestamp = (notes, updatedAt) => {
  if (!notes) return updatedAt ? new Date(updatedAt) : null

  let match = notes.match(/Canceled from SandDrive at ([\d\-T:.Z]+)/)
  if (match && match[1]) {
    return new Date(match[1])
  }

  match = notes.match(/Rerouted at ([\d\-T:.Z]+)/)
  if (match && match[1]) {
    return new Date(match[1])
  }

  return updatedAt ? new Date(updatedAt) : null
}

const PendingBOLsList = ({
  pendingBOLs,
  onSelectBOL,
  selectedBOLId,
  newBOLHighlight,
  acknowledgedBOLIds = [],
  onAcknowledgeBOL = () => {},
  onDismissBOL = () => {},
  cancellationTimeThreshold = 24
}) => {
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false)
  const [bolToDismiss, setBolToDismiss] = useState(null)

  const handleDismissClick = bol => {
    setBolToDismiss(bol)
    setDismissConfirmOpen(true)
  }

  const handleConfirmDismiss = () => {
    if (bolToDismiss) {
      onDismissBOL(bolToDismiss)
    }
    setDismissConfirmOpen(false)
    setBolToDismiss(null)
  }

  const handleCancelDismiss = () => {
    setDismissConfirmOpen(false)
    setBolToDismiss(null)
  }
  if (!pendingBOLs || pendingBOLs.length === 0) {
    return (
      <Box mt={3}>
        <Typography variant="subtitle1">No pending BOLs from SandDrive</Typography>
      </Box>
    )
  }

  const activeBOLs = pendingBOLs.filter(bol => bol.status === 'pending_completion')

  const missingPOBOLs = pendingBOLs.filter(bol => {
    const isMissingPOStatus = bol.status === 'sanddrive_missing_po'
    const isNotAcknowledged = !acknowledgedBOLIds.includes(bol.bolId)

    if (!isMissingPOStatus || !isNotAcknowledged) return false

    const creationTime = new Date(bol.created_at)
    const hoursSinceCreation = (Date.now() - creationTime.getTime()) / (1000 * 60 * 60)

    return hoursSinceCreation < cancellationTimeThreshold
  })

  const cancelledBOLs = pendingBOLs.filter(bol => {
    const isCancelledStatus = bol.status === 'sanddrive_canceled' || bol.status === 'sanddrive_change_pull_point'
    const isNotAcknowledged = !acknowledgedBOLIds.includes(bol.bolId)

    if (!isCancelledStatus || !isNotAcknowledged) return false

    const cancellationTime = extractCancellationTimestamp(bol.notes, bol.updated_at)
    const timeToUse = cancellationTime || new Date(bol.updated_at || bol.created_at)
    const hoursSinceCancellation = (Date.now() - timeToUse.getTime()) / (1000 * 60 * 60)

    return hoursSinceCancellation < cancellationTimeThreshold
  })

  if (activeBOLs.length === 0 && missingPOBOLs.length === 0 && cancelledBOLs.length === 0) {
    return (
      <Box mt={3}>
        <Typography variant="subtitle1">No pending BOLs from SandDrive</Typography>
      </Box>
    )
  }

  const bolsByDriver = activeBOLs.reduce((acc, bol) => {
    const driverKey = bol.driver_name
    if (!acc[driverKey]) {
      acc[driverKey] = []
    }
    acc[driverKey].push(bol)
    return acc
  }, {})

  const driversWithMultipleBOLs = Object.keys(bolsByDriver).filter(driver => bolsByDriver[driver].length > 1)

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>
        Pending BOLs from SandDrive
      </Typography>

      {driversWithMultipleBOLs.length > 0 && (
        <Box mb={2} p={2} bgcolor="warning.main" color="warning.contrastText" borderRadius={1}>
          <Typography variant="body2">
            Note: Pull point changes detected. Multiple BOL previews are shown for updated routing.
          </Typography>
        </Box>
      )}

      {activeBOLs.length > 0 && (
        <Paper style={{ marginBottom: 20 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>BOL ID</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeBOLs.map(bol => {
                const isSelected = selectedBOLId === bol.bolId
                const isNew = newBOLHighlight === bol.bolId
                const hasMultipleBOLs = bolsByDriver[bol.driver_name]?.length > 1

                return (
                  <TableRow
                    key={bol.bolId}
                    style={{
                      backgroundColor: isNew ? '#e8f5e9' : isSelected ? '#e3f2fd' : 'inherit',
                      transition: 'background-color 0.5s ease',
                      borderLeft: hasMultipleBOLs ? '4px solid #ff9800' : 'none'
                    }}
                    className={isNew ? 'pulse-row' : ''}
                  >
                    <TableCell>{bol.bolId}</TableCell>
                    <TableCell>
                      {bol.Purchase_Orders?.purchase_order_number}
                      {hasMultipleBOLs && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          (Updated PO/Pull Point)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{bol.driver_name}</TableCell>
                    <TableCell>{bol.carrier}</TableCell>
                    <TableCell>{createDateString(bol.created_at)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={isSelected}
                          onClick={() => onSelectBOL(bol)}
                        >
                          {isSelected ? 'Selected' : 'Complete'}
                        </Button>
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() => handleDismissClick(bol)}
                          title="Dismiss this BOL"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {missingPOBOLs.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom style={{ color: '#ff6f00' }}>
            BOLs Missing Purchase Orders
          </Typography>
          <Paper style={{ marginBottom: 20, border: '2px solid #ff6f00' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>BOL ID</TableCell>
                  <TableCell>Missing PO Number</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingPOBOLs.map(bol => {
                  // Parse different PO issue types from notes
                  const notFoundMatch = bol.notes?.match(/PO ([\w-]+) not found/)
                  const closedMatch = bol.notes?.match(/PO ([\w-]+) is closed/)
                  const expiredMatch = bol.notes?.match(/PO ([\w-]+) has expired/)

                  const poNumber = notFoundMatch?.[1] || closedMatch?.[1] || expiredMatch?.[1] || 'Unknown'

                  let issueText = 'Purchase Order issue'
                  let actionText = 'Resolve PO issue and retry from SandDrive'

                  if (notFoundMatch) {
                    issueText = 'Purchase Order not found in Logitrac'
                    actionText = `Create PO "${poNumber}" and retry from SandDrive`
                  } else if (closedMatch) {
                    issueText = 'Purchase Order is closed'
                    actionText = `Reopen PO "${poNumber}" or create new PO and retry from SandDrive`
                  } else if (expiredMatch) {
                    issueText = 'Purchase Order has expired'
                    actionText = `Extend PO "${poNumber}" expiry or create new PO and retry from SandDrive`
                  }

                  return (
                    <TableRow
                      key={bol.bolId}
                      style={{
                        backgroundColor: '#fff8e1',
                        borderLeft: '4px solid #ff6f00'
                      }}
                    >
                      <TableCell>{bol.bolId}</TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 'bold', color: '#ff6f00' }}>
                          {poNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{bol.driver_name}</TableCell>
                      <TableCell>{createDateString(bol.created_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error">
                          {issueText}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {actionText}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          color="default"
                          onClick={() => onAcknowledgeBOL(bol.bolId)}
                        >
                          Dismiss
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {cancelledBOLs.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Rescinded BOLs
          </Typography>
          <Paper style={{ marginBottom: 20 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>BOL ID</TableCell>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Rescinded</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancelledBOLs.map(bol => {
                  const cancellationTime = extractCancellationTimestamp(bol.notes, bol.updated_at)
                  const isPullPointChange = bol.status === 'sanddrive_change_pull_point'

                  const hasReplacementBOL = bol.replaced_by_bolid != null

                  const isNonLogitracLocation = isPullPointChange && !hasReplacementBOL

                  const reasonText = isPullPointChange
                    ? hasReplacementBOL
                      ? `Pull point changed - Replaced by BOL #${bol.replaced_by_bolid}`
                      : 'Pull point changed to non-Logitrac location'
                    : 'Fulfillment canceled'

                  return (
                    <TableRow
                      key={bol.bolId}
                      style={{
                        backgroundColor: isNonLogitracLocation ? '#ffebee' : isPullPointChange ? '#fff3e0' : '#ffebee',
                        borderLeft: isNonLogitracLocation
                          ? '4px solid #d32f2f'
                          : isPullPointChange
                          ? '4px solid #ff9800'
                          : '4px solid #f44336'
                      }}
                    >
                      <TableCell>{bol.bolId}</TableCell>
                      <TableCell>{bol.Purchase_Orders?.purchase_order_number}</TableCell>
                      <TableCell>{bol.driver_name}</TableCell>
                      <TableCell>{createDateString(bol.created_at)}</TableCell>
                      <TableCell>{cancellationTime ? createDateString(cancellationTime) : createDateString(bol.updated_at || bol.created_at)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={isNonLogitracLocation ? 'error' : isPullPointChange ? 'textSecondary' : 'error'}
                        >
                          {reasonText}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          color={isNonLogitracLocation ? 'secondary' : 'default'}
                          onClick={() => onAcknowledgeBOL(bol.bolId)}
                        >
                          Dismiss
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
      {/* Dismiss Confirmation Dialog */}
      <Dialog open={dismissConfirmOpen} onClose={handleCancelDismiss} aria-labelledby="dismiss-dialog-title">
        <DialogTitle id="dismiss-dialog-title">Dismiss Pending BOL?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to dismiss pending BOL #{bolToDismiss?.bolId}?
            <br />
            <br />
            <strong>Driver:</strong> {bolToDismiss?.driver_name}
            <br />
            <strong>PO:</strong> {bolToDismiss?.Purchase_Orders?.purchase_order_number}
            <br />
            <br />
            This will hide the BOL from the pending list. The action has no effect on the SandDrive side.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDismiss} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDismiss} color="secondary" variant="contained">
            Dismiss BOL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PendingBOLsList
